import React, { useState, useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import api from '../services/api';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

const containerVariants = {
  animate: { transition: { staggerChildren: 0.03 } }
};

const itemVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

function Activity() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchActivity();
  }, []);

  const fetchActivity = async () => {
    try {
      const response = await api.get('/admin/activity');
      setActivities(response.data.activities);
    } catch (err) {
      setError('Failed to load activity');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'login': return { bg: 'var(--accent-light)', color: 'var(--accent)' };
      case 'register': return { bg: 'var(--success-light)', color: 'var(--success)' };
      case 'logout': return { bg: 'var(--bg-wash)', color: 'var(--text-muted)' };
      case 'subscription': return { bg: 'var(--warning-light)', color: 'var(--warning)' };
      default: return { bg: 'var(--info-light)', color: 'var(--info)' };
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="page-header">
        <h1 className="page-title">Activity Log</h1>
        <p className="page-subtitle">Recent platform activity and events</p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="glass-card" style={{ padding: 0 }}>
        <div style={{
          padding: '18px 24px',
          borderBottom: '1px solid var(--border-light)'
        }}>
          <h3 className="card-title" style={{ margin: 0 }}>
            Recent Activity
            <span style={{
              marginLeft: '10px',
              fontSize: '13px',
              color: 'var(--text-muted)',
              fontWeight: 400
            }}>
              {activities.length} events
            </span>
          </h3>
        </div>

        <motion.div
          style={{ padding: '8px' }}
          variants={containerVariants}
          initial="initial"
          animate="animate"
        >
          {activities.map((activity, index) => {
            const typeStyle = getTypeColor(activity.type);
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '14px 16px',
                  borderRadius: 'var(--radius-sm)',
                  transition: 'background-color 0.15s ease',
                  cursor: 'default'
                }}
                whileHover={{ backgroundColor: 'rgba(99, 102, 241, 0.03)' }}
              >
                {/* Type badge */}
                <span style={{
                  padding: '4px 12px',
                  borderRadius: 'var(--radius-full)',
                  fontSize: '11.5px',
                  fontWeight: 500,
                  backgroundColor: typeStyle.bg,
                  color: typeStyle.color,
                  whiteSpace: 'nowrap',
                  minWidth: '80px',
                  textAlign: 'center'
                }}>
                  {activity.type}
                </span>

                {/* User info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: '13.5px' }}>
                    {activity.first_name} {activity.last_name}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {activity.email}
                  </div>
                </div>

                {/* Timestamp */}
                <div style={{
                  fontSize: '12px',
                  color: 'var(--text-muted)',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}>
                  {new Date(activity.timestamp).toLocaleString()}
                </div>
              </motion.div>
            );
          })}

          {activities.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: 'var(--text-muted)'
            }}>
              No activity recorded yet
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}

export default Activity;
